import { FC, useContext, useEffect, useState } from "react";

import { Save } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConnectConfiguration,
  getOrgConfigPortal,
  patchOrgConfig,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";

const isValidRegex = (pattern: string) => {
  try {
    new RegExp(`^(?:${pattern})$`);
    return true;
  } catch {
    return false;
  }
};

const ConnectSettingsPage: FC = () => {
  const { t } = useTranslation("connect");
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgId = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";
  const canManage = userPermissionChecks(
    currentUser?.permissions?.map((p) => p.title) ?? [],
  ).canManageConnectSettings;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [allowDownload, setAllowDownload] = useState(true);
  const [linkValidityDays, setLinkValidityDays] = useState("");
  const [retentionDays, setRetentionDays] = useState("");
  const [enforceFormat, setEnforceFormat] = useState(false);
  const [pattern, setPattern] = useState("");
  const [example, setExample] = useState("");
  const [description, setDescription] = useState("");
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [nonEmergencyPhone, setNonEmergencyPhone] = useState("");

  useEffect(() => {
    if (!orgId || !canManage) return;
    (async () => {
      setLoading(true);
      try {
        const res = await getOrgConfigPortal({ path: { org_id: orgId } });
        const cc = res.data?.connect_configuration;
        setAllowDownload(cc?.allow_recording_download ?? true);
        setLinkValidityDays(
          cc?.link_validity_days != null ? String(cc.link_validity_days) : "",
        );
        setRetentionDays(
          cc?.retention_days != null ? String(cc.retention_days) : "",
        );
        const fmt = cc?.occurrence_number_format;
        setEnforceFormat(!!fmt);
        setPattern(fmt?.pattern ?? "");
        setExample(fmt?.example ?? "");
        setDescription(fmt?.description ?? "");
        setTagOptions(cc?.reference_tag_options ?? []);
        setNonEmergencyPhone(cc?.non_emergency_phone ?? "");
      } catch {
        toast.error(t("settings.toast.loadFailed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, canManage, t]);

  const patternError =
    enforceFormat && pattern.trim().length > 0 && !isValidRegex(pattern.trim());

  const parseDays = (v: string): number | undefined => {
    const n = parseInt(v.trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };

  const handleSave = async () => {
    if (patternError) {
      toast.error(t("settings.toast.invalidPattern"));
      return;
    }
    if (enforceFormat && !pattern.trim()) {
      toast.error(t("settings.toast.patternRequired"));
      return;
    }
    setSaving(true);
    try {
      // Send the whole object — the backend replaces connect_configuration
      // wholesale, so omitted fields would otherwise be cleared.
      const connect_configuration: ConnectConfiguration = {
        allow_recording_download: allowDownload,
        link_validity_days: parseDays(linkValidityDays) ?? null,
        retention_days: parseDays(retentionDays) ?? null,
        occurrence_number_format:
          enforceFormat && pattern.trim()
            ? {
                pattern: pattern.trim(),
                example: example.trim() || null,
                description: description.trim() || null,
              }
            : null,
        reference_tag_options: tagOptions.length ? tagOptions : null,
        non_emergency_phone: nonEmergencyPhone.trim() || null,
      };
      await patchOrgConfig({
        path: { org_id: orgId },
        body: { connect_configuration },
      });
      toast.success(t("settings.toast.saved"));
    } catch {
      toast.error(t("settings.toast.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {t("settings.title")}
        </Typography>
        <Typography color="text.secondary">
          {t("settings.noPermission")}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        {t("settings.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("settings.subtitle")}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {t("settings.recordings.heading")}
          </Typography>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                />
              }
              label={t("settings.recordings.allowDownload")}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: -1.5 }}
            >
              {t("settings.recordings.allowDownloadHelp")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("settings.recordings.linkValidityLabel")}
                type="number"
                fullWidth
                value={linkValidityDays}
                onChange={(e) => setLinkValidityDays(e.target.value)}
                placeholder="90"
                helperText={t("settings.recordings.linkValidityHelp")}
              />
              <TextField
                label={t("settings.recordings.retentionLabel")}
                type="number"
                fullWidth
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
                placeholder="30"
                helperText={t("settings.recordings.retentionHelp")}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {t("settings.occurrenceNumbers.heading")}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={enforceFormat}
                onChange={(e) => setEnforceFormat(e.target.checked)}
              />
            }
            label={t("settings.occurrenceNumbers.enforce")}
          />
          {enforceFormat && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                label={t("settings.occurrenceNumbers.patternLabel")}
                fullWidth
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                error={patternError}
                helperText={
                  patternError
                    ? t("settings.occurrenceNumbers.patternInvalid")
                    : t("settings.occurrenceNumbers.patternHelp", {
                        example: "\\d{2}-\\d{6}",
                      })
                }
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label={t("settings.occurrenceNumbers.exampleLabel")}
                  fullWidth
                  value={example}
                  onChange={(e) => setExample(e.target.value)}
                  placeholder="24-000123"
                />
                <TextField
                  label={t("settings.occurrenceNumbers.descriptionLabel")}
                  fullWidth
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t(
                    "settings.occurrenceNumbers.descriptionPlaceholder",
                  )}
                />
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {t("settings.referenceTags.heading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("settings.referenceTags.description")}
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={tagOptions}
            onChange={(_, v) => setTagOptions(v as string[])}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option} {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("settings.referenceTags.label")}
                placeholder={t("settings.referenceTags.placeholder")}
              />
            )}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {t("settings.contact.heading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t("settings.contact.description")}
          </Typography>
          <TextField
            label={t("settings.contact.phoneLabel")}
            placeholder="519-555-0199"
            fullWidth
            size="small"
            value={nonEmergencyPhone}
            onChange={(e) => setNonEmergencyPhone(e.target.value)}
          />
        </CardContent>
      </Card>

      <Button
        variant="contained"
        startIcon={<Save />}
        onClick={handleSave}
        disabled={saving || patternError}
      >
        {saving ? t("settings.saving") : t("settings.save")}
      </Button>
    </Box>
  );
};

export default ConnectSettingsPage;
